import { useParams, Navigate, Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import { BOOTH_TYPES, getBoothTypeBySlug, boothFaqs } from "@/data/boothTypes";
import { COMPANY } from "@/data/clientsData";
import { organizationSchema, breadcrumbSchema, faqSchema, itemListSchema, SITE_URL } from "@/lib/seo-schemas";
import { SeoHero, SeoSection, CheckList, FaqList, LinkGrid, QuoteBand } from "@/components/SeoBlocks";

const INDEX_SEO = {
    title: "Trade Show Booth Designs & Prices by Size | Stagehook Global",
    description: "Custom trade show booth designs with 2026 pricing: 10×10 from USD 9,500, 10×20 from USD 19,000, 20×20 islands from USD 48,000. Inline, island, modular rental and country pavilion options.",
    path: "/booth",
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Booth designs", path: "/booth" }]), itemListSchema("Booth designs by size and type", BOOTH_TYPES.map((b) => ({ name: b.name, path: `/booth/${b.slug}` })))],
  };

const BoothIndex = () => {
  return (
    <main data-testid="booth-index-page" className="pb-24">
      <SeoHero eyebrow="Sizes · types · prices" title="Booth designs" accent="by size and type." intro="Transparent 2026 pricing for every footprint from a 10×10 inline to a 30×30 island, plus rental, inline, island and country-pavilion formats." />
      <SeoSection title="By size"><LinkGrid base="/booth" testPrefix="booth" items={BOOTH_TYPES.filter((b) => b.kind === "size").map((b) => ({ slug: b.slug, title: b.name, sub: `${b.price} · ${b.sqft} sq ft` }))} /></SeoSection>
      <SeoSection title="By type"><LinkGrid base="/booth" testPrefix="booth" items={BOOTH_TYPES.filter((b) => b.kind === "type").map((b) => ({ slug: b.slug, title: b.name, sub: b.price }))} /></SeoSection>
      <QuoteBand source="booth-index" />
    </main>
  );
};

const BoothTypePage = () => {
  const { slug } = useParams();
  const b = slug ? getBoothTypeBySlug(slug) : null;
  const faqs = b ? boothFaqs(b) : [];
  const others = b ? BOOTH_TYPES.filter((x) => x.slug !== b.slug).slice(0, 6) : [];

  useSEO(slug ? {
    title: b ? `${b.name}: 2026 Prices & Examples | Stagehook` : "Booth designs | Stagehook Global",
    description: b ? `${b.summary} Turnkey price ${b.price}${b.priceEu ? ` (${b.priceEu} in Europe)` : ""}. 3D renders in 72 hours.` : undefined,
    keywords: b ? `${b.short} design, ${b.short} cost, custom ${b.short}, ${b.short} ideas, trade show ${b.short} price` : undefined,
    image: b?.heroImage,
    path: `/booth/${slug}`,
    jsonLd: b ? [
      organizationSchema(),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Booth designs", path: "/booth" }, { name: b.name, path: `/booth/${b.slug}` }]),
      { "@context": "https://schema.org", "@type": "Product", name: b.name, description: b.summary, image: b.heroImage.startsWith("http") ? b.heroImage : `${SITE_URL}${b.heroImage}`, brand: { "@type": "Organization", name: COMPANY.name }, offers: { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: (b.price.match(/[\d,]+/g) || ["0"])[0].replace(/,/g, ""), highPrice: (b.price.match(/[\d,]+/g) || ["0", "0"])[1]?.replace(/,/g, ""), url: `${SITE_URL}/booth/${b.slug}` } },
      faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a }))),
    ] : undefined,
  } : INDEX_SEO);

  if (!slug) return <BoothIndex />;
  if (!b) return <Navigate to="/404" replace />;

  return (
    <main data-testid="booth-page" className="pb-24">
      <SeoHero eyebrow={b.kind === "size" ? `${b.sqft} sq ft · ${b.sqm} sqm` : "Booth type"} title={b.name.replace(/ (Design|Booth|Booths|Stands|Design & Build)$/, "")} accent={b.kind === "size" ? "— priced." : "."} intro={b.summary} chips={[`USA: ${b.price}`, ...(b.priceEu ? [`Europe: ${b.priceEu}`] : []), "3D renders in 72h", "Fixed price"]} />
      <section className="py-6"><div className="shg-container"><div className="aspect-[21/9] rounded-2xl overflow-hidden"><img src={b.heroImage} alt={b.name} className="w-full h-full object-cover" /></div></div></section>
      <SeoSection title="What's included" testId="booth-includes"><CheckList items={b.includes} /></SeoSection>
      <SeoSection title="Design tips that pay back"><CheckList items={b.tips} /></SeoSection>
      <SeoSection title="Examples from our portfolio" testId="booth-examples"><ul className="space-y-2">{b.examples.map((e) => <li key={e} className="text-[var(--text-soft)]">— {e}</li>)}</ul><Link to="/projects" className="inline-block mt-4 text-sm text-[var(--red)]">See all projects →</Link></SeoSection>
      <QuoteBand expoName={`your show`} source="booth" />
      <SeoSection title={`${b.short.charAt(0).toUpperCase() + b.short.slice(1)} FAQ`} testId="booth-faq"><FaqList faqs={faqs} /></SeoSection>
      <SeoSection title="Other sizes and types"><LinkGrid base="/booth" testPrefix="booth-other" items={others.map((o) => ({ slug: o.slug, title: o.name, sub: o.price }))} /></SeoSection>
    </main>
  );
};

export default BoothTypePage;
